import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Mail, MessageCircleQuestion, LifeBuoy, Search, X } from "lucide-react";
import { PublicShell, useBrand } from "./PublicHome";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tq = () => { const t = new URLSearchParams(window.location.search).get("t") || localStorage.getItem("kl_tenant"); return t ? { headers: { "X-Tenant": t } } : {}; };

const faqFor = (app) => [
  { q: "Come accumulo i punti?", a: `Presenta la tua card ${app} (fisica o il QR nell'app) alla cassa a ogni acquisto: i punti vengono accreditati subito e li vedi nella tua Area Riservata.` },
  { q: "Ho dimenticato la password: come la recupero?", a: "Vai su Accedi → inserisci card o cognome → \"Password dimenticata\". Riceverai un link via WhatsApp o email per crearne una nuova. Il link vale 30 minuti." },
  { q: "Non trovo il numero della mia card", a: "Puoi accedere anche con il cognome: ti mostreremo le card associate con telefono mascherato. In alternativa chiedi in negozio: lo staff la trova dal tuo nome." },
  { q: "Come uso un coupon o riscatto un premio?", a: "Dalla tua Area Riservata apri Coupon o Premi, mostra il codice/QR alla cassa e lo staff lo applica allo scontrino. I coupon hanno una scadenza indicata sulla card." },
  { q: "Posso aggiungere la card al Wallet del telefono?", a: "Sì: nell'Area Riservata trovi i pulsanti \"Aggiungi ad Apple Wallet\" e \"Google Wallet\". Il saldo punti si aggiorna automaticamente." },
  { q: "Non ricevo i messaggi WhatsApp o le email", a: "Controlla che telefono ed email nel tuo profilo siano corretti e che il nostro mittente non finisca nello spam. Se il problema persiste scrivi all'assistenza." },
  { q: "Come modifico o cancello i miei dati?", a: "Dal profilo puoi aggiornare i tuoi dati. Per la cancellazione dell'account o richieste privacy scrivi all'assistenza indicando il numero della card." },
];

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const Mark = ({ text, q }) => {
  if (!q) return text;
  const i = norm(text).indexOf(norm(q));
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="bg-yellow-200 rounded px-0.5">{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

export default function PublicHelp() {
  const { settings, brand } = useBrand();
  const [contacts, setContacts] = useState({});
  const [faq, setFaq] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/contacts`, tq()).then(({ data }) => setContacts(data || {})).catch(() => {});
    axios.get(`${API}/public/faq`, tq()).then(({ data }) => setFaq(data || null)).catch(() => {});
  }, []);
  const app = settings.app_name || "Loyalty Club";
  const items = faq?.items?.length ? faq.items : faqFor(app);
  const query = q.trim();
  const filtered = useMemo(() => {
    if (!query) return items;
    const terms = norm(query).split(/\s+/).filter(Boolean);
    return items.filter((f) => { const hay = norm(`${f.q} ${f.a}`); return terms.every((t) => hay.includes(t)); });
  }, [items, query]);
  const intro = faq?.intro || `Qui trovi le risposte alle domande più frequenti su ${app}. Se non trovi quello che cerchi, scrivici: ti rispondiamo il prima possibile.`;
  const support = settings.support_email || contacts.email || "";
  const mailto = support ? `mailto:${support}?subject=${encodeURIComponent(`Assistenza ${app}`)}&body=${encodeURIComponent("Ciao,\n\nho bisogno di aiuto con:\n\nNumero card (se disponibile):\nNome e cognome:\n")}` : null;
  return (
    <PublicShell title="Aiuto" testid="public-help">
      <div className="rounded-3xl p-5 text-white shadow-md space-y-2" style={{ background: brand }} data-testid="help-intro">
        <div className="flex items-center gap-2"><LifeBuoy className="h-5 w-5" /><span className="font-heading font-bold text-lg" style={{ color: "#fff" }}>Come possiamo aiutarti?</span></div>
        <p className="text-sm text-white/90 whitespace-pre-line" data-testid="help-intro-text">{intro}</p>
      </div>

      <section className="space-y-2" data-testid="help-faq">
        <h2 className="font-heading font-bold text-base md:text-lg flex items-center gap-2"><MessageCircleQuestion className="h-5 w-5" style={{ color: brand }} /> Domande frequenti</h2>
        <div className="relative" data-testid="help-search">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} type="text" inputMode="search" placeholder="Cerca una risposta… (es. password, punti, wallet)"
                 className="w-full h-12 rounded-2xl bg-white ring-1 ring-zinc-200 pl-10 pr-10 text-sm shadow-sm focus:outline-none focus:ring-2 transition-shadow"
                 style={{ "--tw-ring-color": q ? brand : undefined }} aria-label="Cerca nelle domande frequenti" data-testid="help-search-input" />
          {q && <button type="button" onClick={() => setQ("")} aria-label="Cancella ricerca" className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-zinc-100 text-zinc-500 flex items-center justify-center hover:bg-zinc-200" data-testid="help-search-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
        {query && <p className="text-xs text-zinc-500 px-1" data-testid="help-search-count">{filtered.length === 0 ? "Nessuna risposta trovata" : `${filtered.length} ${filtered.length === 1 ? "risposta trovata" : "risposte trovate"}`}</p>}
        {filtered.length > 0 ? (
          <Accordion type="single" collapsible key={query ? `s-${filtered.length}` : "all"} defaultValue={query && filtered.length === 1 ? "faq-0" : undefined} className="rounded-2xl bg-white shadow-sm ring-1 ring-zinc-200 px-4">
            {filtered.map((f, i) => (
              <AccordionItem key={`${f.q}-${i}`} value={`faq-${i}`} data-testid={`help-faq-item-${i}`}>
                <AccordionTrigger className="text-left text-sm font-semibold" data-testid={`help-faq-trigger-${i}`}><span><Mark text={f.q} q={query} /></span></AccordionTrigger>
                <AccordionContent className="text-sm text-zinc-600 whitespace-pre-line" data-testid={`help-faq-content-${i}`}><Mark text={f.a} q={query} /></AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 text-center text-sm text-zinc-500" data-testid="help-search-empty">
            Nessuna domanda corrisponde a "<b>{query}</b>". Prova con parole diverse oppure scrivici qui sotto.
          </div>
        )}
      </section>

      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-zinc-200 space-y-3 text-center" data-testid="help-contact">
        <div className="font-heading font-bold">Non hai trovato la risposta?</div>
        {mailto ? (
          <>
            <a href={mailto} data-testid="help-support-button"
               className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-white font-semibold shadow-sm transition-transform active:scale-[0.98]"
               style={{ background: brand }}>
              <Mail className="h-5 w-5" /> Scrivi al supporto
            </a>
            <p className="text-xs text-zinc-500" data-testid="help-support-email">{support}</p>
          </>
        ) : <p className="text-sm text-zinc-500">Chiedi allo staff del punto vendita: sarà felice di aiutarti.</p>}
      </section>
    </PublicShell>
  );
}
